import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DateFild from '../../components/DateTimePicker/DateFild';
import { FaTrash } from "react-icons/fa";

const severityOptions = [
    { name: 'Negligible', score: 1 },
    { name: 'Minor', score: 2 },
    { name: 'Major', score: 3 },
    { name: 'Hazardous', score: 4 },
    { name: 'Catastrophic', score: 5 }
];

const likelihoodOptions = [
    { name: 'Unlikely', score: 1 },
    { name: 'Rare', score: 2 },
    { name: 'Possible', score: 3 },
    { name: 'Likely', score: 4 },
    { name: 'Frequent', score: 5 }
];

const AircraftIncidentReport = forwardRef(({ reportData, setReportData, localDate, localTime, utcDate, utcTime, airpotname, subIncidentArea, lat, lng, riskAsessmentColor }, ref) => {

    const getRiskColor = (riskRating) => {
        if (Array.isArray(riskAsessmentColor) && riskAsessmentColor.length > 0) {
            if (riskRating >= 16) return riskAsessmentColor[0].RiskColorCode;  //Critical
            if (riskRating >= 10) return riskAsessmentColor[1].RiskColorCode; //High
            if (riskRating >= 5) return riskAsessmentColor[2].RiskColorCode; //Medium
            return riskAsessmentColor[3].RiskColorCode; //Low
        }

        if (riskRating >= 16) return '#f62000';  //Critical
        if (riskRating >= 10) return '#f9c106'; //High
        if (riskRating >= 5) return '#fcff00'; //Medium
        return '#34cd00';                     // Low
    };



    // Initialize state with report data
    const [editedContent, setEditedContent] = useState({
        detailedDescription: reportData?.image_analysis?.description || '',
        shortDescription: reportData?.short_description?.description || '',
        immediateActionsTaken: reportData?.immediate_actions_taken?.description || '',

        whatHappened: reportData?.fiveWAnalysis?.data?.whatHappened || '',

        where: reportData?.fiveWAnalysis?.data?.where || '',

        when: reportData?.fiveWAnalysis?.data?.when || '',

        why: Array.isArray(reportData?.fiveWAnalysis?.data?.why) ?
            reportData?.fiveWAnalysis?.data?.why.join('\n') :
            (reportData?.fiveWAnalysis?.data?.why || ''),

        who: Array.isArray(reportData?.fiveWAnalysis?.data?.whoInvolved) ?
            reportData?.fiveWAnalysis?.data?.whoInvolved.join('\n') :
            (reportData?.fiveWAnalysis?.data?.whoInvolved || ''),

        safetyStandards: Array.isArray(reportData?.standards_referenced?.description) ?
            reportData?.standards_referenced?.description.join('\n') :
            (reportData?.standards_referenced?.description || ''),

        hazardMapping: reportData?.hazard_mapping?.description || '',

        lessonsLearned: reportData?.lessons_learned?.description || '',

        parallelCase: reportData?.real_life_example?.description || '',
    });

    // Initialize risk matrix with proper structure
    const [riskMatrix, setRiskMatrix] = useState(() => {
        if (reportData?.risk_matrix?.data?.length > 0) {
            return reportData.risk_matrix.data.map(item => {
                const severityScore = parseInt(item.SeverityScore || item.severityScore || 0);
                const likelihoodScore = parseInt(item.LikelihoodScore || item.likelihoodScore || 0);
                const rating = parseInt(item.RiskRating || item.riskRating || severityScore * likelihoodScore);

                return {
                    hazard: item.Hazard || item.hazard,
                    severityName: item.SeverityName || item.severityName ||
                        (severityOptions.find(s => s.score === severityScore)?.name || ''),
                    severityScore: severityScore,
                    likelihoodName: item.LikelihoodName || item.likelihoodName ||
                        (likelihoodOptions.find(l => l.score === likelihoodScore)?.name || ''),
                    likelihoodScore: likelihoodScore,
                    riskRating: rating,
                    riskCategory: item.RiskCategory || item.riskCategory ||
                        (rating >= 16 ? 'Critical' :
                            rating >= 10 ? 'High' :
                                rating >= 5 ? 'Medium' : 'Low'),
                    riskCategoryColor: getRiskColor(rating)
                };
            });
        }
        return [{
            hazard: '',
            severityName: '',
            severityScore: 0,
            likelihoodName: '',
            likelihoodScore: 0,
            riskRating: 0,
            riskCategory: 'Low',
            riskCategoryColor: '#b5dfb5'
        }];
    });

    // Initialize corrective actions
    const [correctiveActions, setCorrectiveActions] = useState(
        reportData?.corrective_action_plan?.data?.length > 0
            ? reportData.corrective_action_plan.data.map(item => ({
                department: item.Department || item.department,
                action: item.Action || item.action,
                deadline: item.Deadline || item.deadline,
                responsible: item.Responsible || item.responsible,
                escalation: item.Escalation || item.escalation,
                closureCriteria: item.ClosureCriteria || item.closureCriteria
            }))
            : [{ department: '', action: '', deadline: '', responsible: '', escalation: '', closureCriteria: '' }]
    );

    // Handle content changes
    const handleContentChange = (field, value) => {
        setEditedContent(prev => ({ ...prev, [field]: value }));
    };

    // Handle risk matrix changes
    const handleRiskMatrixChange = (index, field, value) => {
        const updatedMatrix = [...riskMatrix];
        updatedMatrix[index] = { ...updatedMatrix[index], [field]: value };

        // If severity or likelihood changed, update the corresponding name/score
        if (field === 'severityName') {
            const selectedSeverity = severityOptions.find(s => s.name === value);
            if (selectedSeverity) {
                updatedMatrix[index].severityScore = selectedSeverity.score;
            }
        } else if (field === 'severityScore') {
            const selectedSeverity = severityOptions.find(s => s.score === parseInt(value));
            if (selectedSeverity) {
                updatedMatrix[index].severityName = selectedSeverity.name;
            }
        } else if (field === 'likelihoodName') {
            const selectedLikelihood = likelihoodOptions.find(l => l.name === value);
            if (selectedLikelihood) {
                updatedMatrix[index].likelihoodScore = selectedLikelihood.score;
            }
        } else if (field === 'likelihoodScore') {
            const selectedLikelihood = likelihoodOptions.find(l => l.score === parseInt(value));
            if (selectedLikelihood) {
                updatedMatrix[index].likelihoodName = selectedLikelihood.name;
            }
        }

        // Recalculate risk rating and category when severity or likelihood changes
        if (['severityName', 'severityScore', 'likelihoodName', 'likelihoodScore'].includes(field)) {
            const severityVal = updatedMatrix[index].severityScore;
            const likelihoodVal = updatedMatrix[index].likelihoodScore;
            const rating = severityVal * likelihoodVal;

            let category = 'Low';
            if (rating >= 16) category = 'Critical';
            else if (rating >= 10) category = 'High';
            else if (rating >= 5) category = 'Medium';

            updatedMatrix[index].riskRating = rating;
            updatedMatrix[index].riskCategory = category;
            updatedMatrix[index].riskCategoryColor = getRiskColor(rating);
        }

        setRiskMatrix(updatedMatrix);
    };

    // Add new risk matrix row
    const addRiskMatrixRow = () => {
        setRiskMatrix([...riskMatrix, {
            hazard: '',
            severityName: '',
            severityScore: 0,
            likelihoodName: '',
            likelihoodScore: 0,
            riskRating: 0,
            riskCategory: 'Low',
            riskCategoryColor: '#66ff66'
        }]);
    };

    // Remove risk matrix row
    const removeRiskMatrixRow = (index) => {
        if (riskMatrix.length <= 1) return;
        const updatedMatrix = [...riskMatrix];
        updatedMatrix.splice(index, 1);
        setRiskMatrix(updatedMatrix);
    };

    // Handle corrective action changes
    const handleCorrectiveActionChange = (index, field, value) => {
        const updatedActions = [...correctiveActions];
        updatedActions[index] = { ...updatedActions[index], [field]: value };
        setCorrectiveActions(updatedActions);
    };

    // Add new empty action
    const addCorrectiveAction = () => {
        setCorrectiveActions([
            ...correctiveActions,
            { department: '', action: '', deadline: '', responsible: '', escalation: '', closureCriteria: '' }
        ]);
    };

    // Remove an action
    const removeCorrectiveAction = (index) => {
        if (correctiveActions.length <= 1) return;
        const updatedActions = [...correctiveActions];
        updatedActions.splice(index, 1);
        setCorrectiveActions(updatedActions);
    };

    // Expose save handler via ref
    useImperativeHandle(ref, () => ({
        handleSave: () => {
            try {
                // Transform risk matrix to original format
                const formattedRiskMatrix = riskMatrix.map(risk => ({
                    Hazard: risk.hazard,
                    SeverityName: risk.severityName,
                    SeverityScore: risk.severityScore,
                    LikelihoodName: risk.likelihoodName,
                    LikelihoodScore: risk.likelihoodScore,
                    RiskRating: risk.riskRating,
                    RiskCategory: risk.riskCategory,
                    RiskCategoryColor: risk.riskCategoryColor
                }));

                // Transform corrective actions to original format
                const formattedCorrectiveActions = correctiveActions.map(action => ({
                    Department: action.department,
                    Action: action.action,
                    Deadline: action.deadline,
                    Responsible: action.responsible,
                    Escalation: action.escalation,
                    ClosureCriteria: action.closureCriteria
                }));

                // Create updated sections object
                const payload = {
                    ...reportData,
                    image_analysis: {
                        ...reportData.image_analysis,
                        description: editedContent.detailedDescription
                    },
                    short_description: {
                        ...reportData.short_description,
                        description: editedContent.shortDescription
                    },
                    immediate_actions_taken: {
                        ...reportData.immediate_actions_taken,
                        description: editedContent.immediateActionsTaken
                    },
                    fiveWAnalysis: {
                        ...reportData.fiveWAnalysis,
                        data: {
                            whatHappened: editedContent.whatHappened,
                            where: editedContent.where,
                            when: editedContent.when,
                            why: editedContent.why.split('\n').filter(item => item.trim()),
                            whoInvolved: editedContent.who.split('\n').filter(item => item.trim())
                        }
                    },
                    risk_matrix: {
                        ...reportData.risk_matrix,
                        data: formattedRiskMatrix
                    },
                    corrective_action_plan: {
                        ...reportData.corrective_action_plan,
                        data: formattedCorrectiveActions
                    },
                    standards_referenced: {
                        ...reportData.standards_referenced,
                        description: editedContent.safetyStandards.split('\n').filter(item => item.trim())
                    },
                    hazard_mapping: {
                        ...reportData.hazard_mapping,
                        description: editedContent.hazardMapping
                    },
                    lessons_learned: {
                        ...reportData.lessons_learned,
                        description: editedContent.lessonsLearned
                    },
                    real_life_example: {
                        ...reportData.real_life_example,
                        description: editedContent.parallelCase
                    }
                }
                setReportData(payload);
                return true; // Indicate success
            } catch (error) {
                console.error('Save failed:', error);
                return false; // Indicate failure
            }
        }
    }));

    return (
        <div className="report-container">
            <header className="report-header">
                <h1>{reportData.report_title}</h1>
                <div className="report-meta">
                    <span>Status: Draft</span>
                    <span>Last Updated: {new Date().toLocaleString()}</span>
                </div>
            </header>

            <section className="report-section">
                <h2>Short Description</h2>
                <CKEditor
                    editor={ClassicEditor}
                    data={editedContent?.shortDescription}
                    config={{
                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                    }}
                    onChange={(event, editor) =>
                        handleContentChange('shortDescription', editor.getData())
                    }
                />
            </section>

            <section className="report-section">
                <h2>Detailed Description</h2>
                <div className="image-description">
                    <CKEditor
                        editor={ClassicEditor}
                        data={editedContent?.detailedDescription}
                        config={{
                            toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                        }}
                        onChange={(event, editor) => {
                            handleContentChange('detailedDescription', editor.getData())
                        }}
                    />
                </div>
            </section>

            <section className="report-section">
                <h2>Immediate Actions Taken</h2>
                <div className="image-description">
                    <CKEditor
                        editor={ClassicEditor}
                        data={editedContent?.immediateActionsTaken}
                        config={{
                            toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                        }}
                        onChange={(event, editor) => {
                            handleContentChange('immediateActionsTaken', editor.getData())
                        }}
                    />
                </div>
            </section>

            <section className="report-section customtable">
                <h2>5W Analysis</h2>
                <table className='table'>
                    <tbody>
                        <tr>
                            <th>What</th>
                            <td>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={editedContent.whatHappened}
                                    config={{
                                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                                    }}
                                    onChange={(event, editor) =>
                                        handleContentChange('whatHappened', editor.getData())
                                    }
                                />
                            </td>
                        </tr>
                        <tr>
                            <th>Where</th>
                            <td>
                                {/* <CKEditor
                                    editor={ClassicEditor}
                                    data={editedContent.where}
                                    config={{
                                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                                        readOnly: true,
                                    }}
                                    disabled={true}
                                    onChange={(event, editor) =>
                                        handleContentChange('where', editor.getData())
                                    }
                                /> */}
                                <div className='dateimebox'>
                                    <p>{airpotname}</p>
                                    <p>{subIncidentArea}</p>
                                    <p>{lat} {lng}</p>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>When</th>
                            <td>
                                {/* <CKEditor
                                    editor={ClassicEditor}
                                    data={editedContent.when}
                                    config={{
                                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                                        readOnly: true,
                                    }}
                                    disabled={true}
                                    onChange={(event, editor) =>
                                        handleContentChange('when', editor.getData())
                                    }
                                /> */}
                                <div className='dateimebox'>
                                    <p>Local Date Time :-  {localDate} {localTime} </p>
                                    <p>UTC Date Time :- {utcDate} {utcTime} </p>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>Why</th>
                            <td>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={editedContent.why}
                                    config={{
                                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                                    }}
                                    onChange={(event, editor) =>
                                        handleContentChange('why', editor.getData())
                                    }
                                />
                            </td>
                        </tr>
                        <tr>
                            <th>Who</th>
                            <td>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={editedContent.who}
                                    config={{
                                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                                    }}
                                    onChange={(event, editor) =>
                                        handleContentChange('who', editor.getData())
                                    }
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section className="report-section">
                <h2>Risk Matrix</h2>
                <div className='RiskscoreList'>
                    <ul>
                        {riskAsessmentColor.map((item) => (
                            <li key={item.ToScore}><span style={{ backgroundColor: item.RiskColorCode }}></span> {item.RiskDes}</li>
                        ))}
                    </ul>
                </div>
                <div className="table-responsive">
                <table className="risk-matrix">
                    <thead>
                        <tr>
                            <th>Hazard</th>
                            <th>Severity</th>
                            <th>Likelihood</th>
                            <th>Risk Rating</th>
                            <th>Risk Category</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {riskMatrix.map((risk, index) => (
                            <tr key={index}>
                                <td style={{ backgroundColor: risk.riskCategoryColor }}>
                                    <textarea
                                        type="text"
                                        className="form-control"
                                        value={risk.hazard}
                                        onChange={e => handleRiskMatrixChange(index, 'hazard', e.target.value)}
                                        placeholder="Describe hazard"
                                    />
                                </td>
                                <td>
                                    <select
                                        value={risk.severityName}
                                        className="form-control"
                                        onChange={e => handleRiskMatrixChange(index, 'severityName', e.target.value)}
                                    >
                                        <option value="" disabled={true}>Select Severity</option>
                                        {severityOptions.map(option => (
                                            <option key={option.name} value={option.name}>
                                                {option.name} ({option.score})
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={risk.likelihoodName}
                                        className="form-control"
                                        onChange={e => handleRiskMatrixChange(index, 'likelihoodName', e.target.value)}
                                    >
                                        <option value="" disabled={true}>Select Likelihood</option>
                                        {likelihoodOptions.map(option => (
                                            <option key={option.name} value={option.name}>
                                                {option.name} ({option.score})
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td>{risk.riskRating}</td>
                                <td>
                                    <div className='chips' style={{ backgroundColor: risk.riskCategoryColor }}> {risk.riskCategory}</div>
                                </td>
                                <td>
                                    <button
                                        onClick={() => removeRiskMatrixRow(index)}
                                        className="remove-btn"
                                        disabled={riskMatrix.length <= 1}
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <button onClick={addRiskMatrixRow} className="btn btn-orange">+ Add New Risk</button>
            </section>

            <section className="report-section">
                <h2>Recommendation Action Plan</h2>
                <div className="table-responsive">
                <table className="corrective-actions-table">
                    <thead>
                        <tr>
                            <th>Department</th>
                            <th>Action</th>
                            <th>Deadline</th>
                            <th>Responsible</th>
                            <th>Escalation</th>
                            <th>Closure Criteria</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {correctiveActions.map((action, index) => (
                            <tr key={index}>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={action.department}
                                        onChange={(e) => handleCorrectiveActionChange(index, 'department', e.target.value)}
                                        placeholder="Enter department"
                                    />
                                </td>
                                <td>
                                    <textarea
                                        value={action.action}
                                        className="form-control"
                                        onChange={(e) => handleCorrectiveActionChange(index, 'action', e.target.value)}
                                        placeholder="Describe action"
                                        rows={2}
                                    />
                                </td>
                                <td>
                                    <DateFild
                                        value={action.deadline}
                                        onChange={(date) => handleCorrectiveActionChange(index, 'deadline', date)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={action.responsible}
                                        onChange={(e) => handleCorrectiveActionChange(index, 'responsible', e.target.value)}
                                        placeholder="Responsible person"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={action.escalation}
                                        onChange={(e) => handleCorrectiveActionChange(index, 'escalation', e.target.value)}
                                        placeholder="Escalation path"
                                    />
                                </td>
                                <td>
                                    <textarea
                                        value={action.closureCriteria}
                                        className="form-control"
                                        onChange={(e) => handleCorrectiveActionChange(index, 'closureCriteria', e.target.value)}
                                        placeholder="Closure criteria"
                                        rows={2}
                                    />
                                </td>
                                <td>
                                    <button
                                        onClick={() => removeCorrectiveAction(index)}
                                        className="remove-btn"
                                        disabled={correctiveActions.length <= 1}
                                    ><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <button onClick={addCorrectiveAction} className="btn btn-orange">+ Add New Action</button>
            </section>


            <section className="report-section">
                <h2>Safety Standards Referenced</h2>
                <CKEditor
                    editor={ClassicEditor}
                    data={editedContent.safetyStandards}
                    config={{
                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                    }}
                    onChange={(event, editor) =>
                        handleContentChange('safetyStandards', editor.getData())
                    }
                />
            </section>

            <section className="report-section">
                <h2>Hotspot / Hazard Mapping Insight</h2>
                <CKEditor
                    editor={ClassicEditor}
                    data={editedContent.hazardMapping}
                    config={{
                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                    }}
                    onChange={(event, editor) =>
                        handleContentChange('hazardMapping', editor.getData())
                    }
                />
            </section>

            <section className="report-section">
                <h2>Lessons Learned</h2>
                <CKEditor
                    editor={ClassicEditor}
                    data={editedContent.lessonsLearned}
                    config={{
                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                    }}
                    onChange={(event, editor) =>
                        handleContentChange('lessonsLearned', editor.getData())
                    }
                />
            </section>

            <section className="report-section">
                <h2>Real-Life Parallel Example</h2>
                <CKEditor
                    editor={ClassicEditor}
                    data={editedContent.parallelCase}
                    config={{
                        toolbar: ['undo', 'redo', 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
                    }}
                    onChange={(event, editor) =>
                        handleContentChange('parallelCase', editor.getData())
                    }
                />
            </section>
        </div>
    );
});

export default AircraftIncidentReport;